import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { from, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { McpService } from './mcp.service';
import { FileSystemService } from './file-system.service';
import { QueueService } from './queue.service';
import { RepoService } from './repo.service';
import { UserSettingsService } from './user-settings.service';

/** `sas <path>.sas`: run a workspace program on the remote SAS server. */
const SAS_COMMAND = /^sas\s+(\S+\.sas)$/i;

interface SasWorkspaceRun {
  exit_code: number | null;
  log: string;
  lst: string;
  written: string[];
}


@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private baseUrl = 'none';

  constructor(
    private http: HttpClient,
    private queue: QueueService,
    private userSettings: UserSettingsService,
    private injector: Injector,
  ) {
    this.userSettings.serverUrl$.subscribe((url) => {
      if (url) {
        this.baseUrl = url;
      }
    });
  }

  private get mcp(): McpService {
    return this.injector.get(McpService);
  }

  command(command: string, viewOutput: boolean = false): Observable<string> {
    const sas = command.trim().match(SAS_COMMAND);
    const request = sas
      ? this.runSas(sas[1])
      : this.http.get<string>(`${this.baseUrl}/shell?cmd=${command}`);
    return request.pipe(
      tap({
        next: (response) => {
          this.queue.add({
            type: 'command',
            command,
            output: (response as string),
            viewOutput: viewOutput,
          });
        },
        error: (error) => {
          this.queue.add({
            type: 'command',
            command,
            output: error.error.detail,
            viewOutput: viewOutput,
          });
        }
      })
    );
  }

  /**
   * The workspace container has no SAS, so `sas <file>.sas` goes to the backend,
   * which runs it on the SAS server with the program's folder as its inputs and
   * saves the log, listing and outputs next to the program.
   */
  private runSas(scriptPath: string): Observable<string> {
    const repo = this.injector.get(RepoService).repo();
    // The agent is told the workspace lives at `/<root name>` (e.g. /workspace),
    // so it writes absolute paths; the backend wants them root-relative.
    const root = `/${this.injector.get(FileSystemService).rootNode?.name}/`;
    if (scriptPath.startsWith(root)) {
      scriptPath = scriptPath.slice(root.length);
    }
    return this.http
      .post<SasWorkspaceRun>(`${environment.backendUrl}/sas-runner/run-workspace`, {
        repo_id: repo?.id,
        script_path: scriptPath,
        workspace_server_url: repo?.url ?? null,
      })
      .pipe(
        map((r) =>
          `${r.log}\nSAS exit code: ${r.exit_code ?? 'killed (timeout)'}\nWrote: ${r.written.join(', ')}\n`,
        ),
      );
  }

  view(filename: string): Observable<unknown> {
    return from(this.mcp.client.callTool(
      { name: "summary", arguments: { filepath: filename } }
    )).pipe(
      tap({
        next: (response) => {
          const content = (response.content as Array<{ type: string; text: string }>);
          if (!response.isError) {
            this.queue.add({
              type: 'view',
              filename,
              output: content[0].text,
            });
          }
          else {
            this.queue.add({
              type: 'view',
              filename,
              output: content[0].text,
            });
          }
        },
        error: (error) => {
          this.queue.add({
            type: 'view',
            filename,
            output: "Some error happened connecting with user's machine. Let user know and stop here until they fix it.\n" + JSON.stringify(error),
          });
        },
      })
    );
  }

  createFile(filename: string, content: string): Observable<unknown> {
    return this.http
      .post<{output: string}>(`${this.baseUrl}/create-file`, { file_path: filename, content })
      .pipe(
        tap({
          next: (response) => {
            this.queue.add({
              type: 'createFile',
              filename,
              content,
              output: response.output,
            });
          },
          error: (error) => {
            this.queue.add({
              type: 'createFile',
              filename,
              content,
              output: error.error.detail,
            });
          }
        })
      );
  }

  treeString(path: string, recursive: boolean = true): Observable<string> {
    return this.http.get<string>(`${this.baseUrl}/tree-string`, { params: { path, recursive } });
  }
}
